import { Component, lazy, Suspense, useId, useState, type ReactNode } from 'react';
import { Alert, Box, Button, Collapse, Dialog, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';
import OpenInNew from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import type { HotelPublicContent } from '@bandido/shared/types/hotels/hotel-content';
import { googleMapsLocationUrl, publishedHotelPoint } from './hotelLocationPoint';

const HotelLocationMap = lazy(() => import('./HotelLocationMap'));

export class LocationMapBoundary extends Component<{ children: ReactNode; fallback: ReactNode }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export function HotelLocation({
  content,
  name,
  inline = false,
  loading = false,
}: {
  content?: HotelPublicContent | null;
  name: string;
  inline?: boolean;
  loading?: boolean;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);
  const point = publishedHotelPoint(content);
  if (!point) {
    return loading ? null : (
      <Typography variant="caption" color="text.secondary">
        {t('hotelSearch.exactLocationUnknown')}
      </Typography>
    );
  }
  const map = (
    <Stack gap={1}>
      <LocationMapBoundary fallback={<Alert severity="warning">{t('hotelSearch.mapUnavailable')}</Alert>}>
        <Suspense fallback={<Typography role="status">{t('hotelSearch.mapLoading')}</Typography>}>
          <HotelLocationMap key={`${point.latitude}:${point.longitude}`} point={point} name={name} />
        </Suspense>
      </LocationMapBoundary>
      <Button
        component="a"
        href={googleMapsLocationUrl(point)}
        target="_blank"
        rel="noopener noreferrer"
        endIcon={<OpenInNew />}
        aria-label={t('hotelSearch.openExternalMapLabel')}
        sx={{ alignSelf: 'flex-start', minHeight: 44 }}
      >
        {t('hotelSearch.openExternalMap')}
      </Button>
    </Stack>
  );
  if (inline) {
    return (
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="subtitle1">{t('hotelSearch.locationHeading')}</Typography>
          <Button onClick={() => setOpen(!open)} aria-expanded={open} aria-controls={id} sx={{ minHeight: 44 }}>
            {t(open ? 'hotelSearch.hideMap' : 'hotelSearch.showMap')}
          </Button>
        </Stack>
        <Collapse in={open} id={id} unmountOnExit>
          {map}
        </Collapse>
      </Box>
    );
  }
  return (
    <>
      <Button startIcon={<LocationOnOutlined />} onClick={() => setOpen(true)} sx={{ flexShrink: 0, minHeight: 44 }}>
        {t('hotelSearch.viewLocation')}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby={`${id}-title`}
        PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}
      >
        <DialogTitle id={`${id}-title`}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
            <span>{t('hotelSearch.hotelMap', { name })}</span>
            <Button onClick={() => setOpen(false)} sx={{ flexShrink: 0, minHeight: 44 }}>
              {t('hotelSearch.closeMap')}
            </Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {[content?.location?.address, content?.location?.city, content?.location?.country_code]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
          {open && map}
        </DialogContent>
      </Dialog>
    </>
  );
}
